import { cn } from "@/lib/utils";
import type { MutableRefObject, ReactNode } from "react";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const SCROLL_THRESHOLD = 8;
const FADE_SIZE = 24;

/** Which way the content can still be scrolled, as the fade sees it. */
export type ScrollableEdges = {
  readonly canScrollUp: boolean;
  readonly canScrollDown: boolean;
};

/**
 * A scrollbar-less scroll area that fades its own content out on the edge it
 * can still scroll towards — the only visible sign that more is hidden, once
 * the scrollbar is gone.
 *
 * A mask rather than a gradient overlay: the fade has to work on whatever sits
 * behind the container (a card, the chat home's tinted background), which an
 * overlay hard-coding `from-background` does not.
 *
 * `autoScroll` pins the view to the bottom as children stream in.
 *
 * `onScrollableEdgesChange` reports the same two booleans the fade is drawn
 * from, for a caller that wants a stronger affordance than the fade on top of
 * it. It has to live outside this element: the mask applies to every child,
 * so a hint pinned to the bottom edge from the inside would be faded out by
 * the very gradient it is there to explain.
 */
export const ScrollContainer = memo(
  forwardRef<
    HTMLDivElement,
    {
      children: ReactNode;
      autoScroll?: boolean;
      className?: string;
      onScrollableEdgesChange?: (edges: ScrollableEdges) => void;
    }
  >((
    { children, autoScroll = false, className, onScrollableEdgesChange },
    ref,
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    // Held in a ref so an inline callback does not change `updateShadows`'
    // identity on every render, which would re-subscribe the scroll listener
    // each time. Declared before the effects that call `updateShadows`, so it
    // is already current by the time they run.
    const notifyRef = useRef(onScrollableEdgesChange);
    useEffect(() => {
      notifyRef.current = onScrollableEdgesChange;
    }, [onScrollableEdgesChange]);

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        (innerRef as MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === "function") ref(node);
        else if (ref)
          (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    const updateShadows = useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      const edges = {
        canScrollUp: el.scrollTop > SCROLL_THRESHOLD,
        canScrollDown:
          el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_THRESHOLD,
      };
      setCanScrollUp(edges.canScrollUp);
      setCanScrollDown(edges.canScrollDown);
      notifyRef.current?.(edges);
    }, []);

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      el.addEventListener("scroll", updateShadows, { passive: true });
      updateShadows();
      return () => el.removeEventListener("scroll", updateShadows);
    }, [updateShadows]);

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      if (autoScroll) {
        el.scrollTop = el.scrollHeight;
      }
      updateShadows();
    }, [children, autoScroll, updateShadows]);

    const maskImage = useMemo(() => {
      const top = canScrollUp ? "transparent" : "#000";
      const bottom = canScrollDown ? "transparent" : "#000";
      return `linear-gradient(to bottom, ${top} 0, #000 ${FADE_SIZE}px, #000 calc(100% - ${FADE_SIZE}px), ${bottom} 100%)`;
    }, [canScrollUp, canScrollDown]);

    return (
      <div
        ref={setRefs}
        data-slot="scroll-container"
        className={cn(
          // Vertical only: `overflow-y-auto` alone makes the browser compute
          // `overflow-x` as `auto` too, so a child a pixel too wide — a chip
          // lifted on hover, a long line — would scroll sideways under a fade
          // that only ever masks the top and bottom edges.
          "overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
          className,
        )}
        style={{ WebkitMaskImage: maskImage, maskImage }}
      >
        {children}
      </div>
    );
  }),
);
ScrollContainer.displayName = "ScrollContainer";
